
import { Directive, ElementRef, Renderer2, NgZone, OnInit, OnDestroy } from '@angular/core';

@Directive({
  selector: '[overflowShadow]', // use as an attribute on .inner-content
  standalone: true // Mark as standalone if not part of a module
})

export class OverflowShadowDirective implements OnInit, OnDestroy {
  private resizeObserver?: ResizeObserver;
  private scrollUnlisten?: () => void;

  constructor(
    private elRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const el = this.elRef.nativeElement;

    // Initial compute
    this.updateShadow(el);

    // Observe size changes (content or layout)
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateShadow(el));
      this.resizeObserver.observe(el);
    }

    // Update on scroll (optional: needed if you want fade to hide at bottom)
    this.ngZone.runOutsideAngular(() => {
      this.scrollUnlisten = this.renderer.listen(el, 'scroll', () => this.updateShadow(el));
      // Also update on window resize for good measure
      window.addEventListener('resize', this.windowResizeHandler, { passive: true });
    });
  }

  ngOnDestroy(): void {
      this.resizeObserver?.disconnect();
      this.scrollUnlisten?.();
      window.removeEventListener('resize', this.windowResizeHandler);
  }

  private windowResizeHandler: () => void = () => {
      this.updateShadow(this.elRef.nativeElement);
  };

  private updateShadow(el: HTMLElement): void {

    // Determine if we need to show the shadow
    const hasOverflow = (el.scrollHeight - el.clientHeight) > 1;

    // Check if not at bottom
    const notAtBottom = (el.scrollTop + el.clientHeight) < (el.scrollHeight - 1);

    // Decide if shadow should be shown
    const shouldShow = hasOverflow && notAtBottom;

    // Also toggle on the non-scrolling parent (for the overlay)
    const parent = el.parentElement;
    if (parent) {
      if (shouldShow) {
        this.renderer.addClass(parent, 'has-inner-shadow');
      } else {
        this.renderer.removeClass(parent, 'has-inner-shadow');
      }
    }
  }

}
